import type { Locale } from "./content"

export type ProjectStatus = "active" | "archived" | "in-progress"

export type Project = {
  id: string
  title: Record<Locale, string>
  description: Record<Locale, string>
  tech: string[]
  status: ProjectStatus
  /** Featured projects lead the section with a large plate. */
  featured?: boolean
  link?: string
  internal?: boolean
  logoImg?: string
  logoSlug: string
  coverImg?: string
}

export const projects: Project[] = [
  {
    id: "nexo",
    logoSlug: "NX",
    featured: true,
    coverImg: "/projects/nexo-map.jpg",
    title: {
      en: "Nexo",
      pt: "Nexo",
      de: "Nexo",
    },
    description: {
      en: "All of Portugal's public transport on one live map. Bus, metro, train, tram and ferry from 34 operators, with door-to-door journeys planned across every operator.",
      pt: "Todos os transportes públicos de Portugal num só mapa ao vivo. Autocarro, metro, comboio, elétrico e barco de 34 operadores, com viagens de porta a porta planeadas entre todos os operadores.",
      de: "Der gesamte öffentliche Verkehr Portugals auf einer Live-Karte. Bus, Metro, Zug, Tram und Fähre von 34 Betreibern, mit Routenplanung von Tür zu Tür über alle Betreiber hinweg.",
    },
    tech: ["Go", "TypeScript", "Svelte", "deck.gl", "Mapbox GL"],
    status: "active",
    internal: true,
  },
  {
    id: "tools",
    logoSlug: "T",
    featured: true,
    logoImg: "/projects/tools-logo.jpg",
    coverImg: "/projects/tools-cover.jpg",
    title: {
      en: "Tools",
      pt: "Tools",
      de: "Tools",
    },
    description: {
      en: "33 developer and design utilities in one keyboard-first workspace. JSON, SQL, CSS, JWT, regex, QR and more, running in the browser with no account and no tracking.",
      pt: "33 utilitários de desenvolvimento e design num só espaço, pensado para o teclado. JSON, SQL, CSS, JWT, regex, QR e mais, a correr no browser, sem conta e sem tracking.",
      de: "33 Entwickler- und Design-Tools in einem tastaturorientierten Workspace. JSON, SQL, CSS, JWT, Regex, QR und mehr, direkt im Browser, ohne Konto und ohne Tracking.",
    },
    tech: ["Next.js", "React", "TypeScript", "Tailwind CSS", "Playwright"],
    status: "active",
    internal: true,
  },
  {
    id: "deadletter",
    logoSlug: "DL",
    logoImg: "/projects/deadletter.png",
    coverImg: "/projects/deadletter-ui.png",
    title: {
      en: "Dead Letter",
      pt: "Dead Letter",
      de: "Dead Letter",
    },
    description: {
      en: "A tiny SMTP sink for local development. Built for debugging, observability, and peace of mind. Where emails go to rest.",
      pt: "Um pequeno SMTP sink para desenvolvimento local. Construido para debugging, observabilidade e tranquilidade. Onde os emails vao descansar.",
      de: "Ein kleiner SMTP-Sink fur die lokale Entwicklung. Entwickelt fur Debugging, Observability und Seelenfrieden. Wo E-Mails zur Ruhe kommen.",
    },
    tech: ["Go", "SMTP", "Docker", "Next.js", "REST API"],
    status: "active",
    internal: true,
  },
  {
    id: "pulse",
    logoSlug: "PU",
    logoImg: "/projects/pulse-logo.jpg",
    coverImg: "/projects/pulse-ui.png",
    title: {
      en: "Pulse",
      pt: "Pulse",
      de: "Pulse",
    },
    description: {
      en: "Pulse is a minimal macOS menu bar system monitor that surfaces CPU, memory, disk, network, and temperature at a glance.",
      pt: "Pulse e um monitor de sistema para macOS na barra de menu que mostra CPU, memoria, disco, rede e temperatura num so olhar.",
      de: "Pulse ist ein minimalistischer macOS-Menubar-Monitor, der CPU, Speicher, Laufwerk, Netzwerk und Temperatur auf einen Blick anzeigt.",
    },
    tech: ["Tauri", "Rust", "React", "Vite", "Tailwind CSS"],
    status: "active",
    internal: true,
  },
  /* The following projects are commented out.
  {
    id: "api-gateway",
    logoSlug: "AG",
    coverImg: "/projects/api-gateway.jpg",
    title: {
      en: "API Gateway Service",
      pt: "Servico API Gateway",
      de: "API-Gateway-Dienst",
    },
    description: {
      en: "A high-performance API gateway built with Spring Boot, handling rate limiting, authentication, and request routing for microservice architectures.",
      pt: "Um API gateway de alta performance construido com Spring Boot, gerindo rate limiting, autenticacao e roteamento de pedidos para arquiteturas de microsservicos.",
      de: "Ein leistungsstarkes API-Gateway, entwickelt mit Spring Boot, das Rate Limiting, Authentifizierung und Request-Routing fur Microservice-Architekturen verwaltet.",
    },
    tech: ["Java", "Spring Boot", "Redis", "Docker"],
    status: "active",
    link: "https://github.com/peixotomdb",
  },
  {
    id: "event-platform",
    logoSlug: "EP",
    coverImg: "/projects/event-platform.jpg",
    title: {
      en: "Event-Driven Platform",
      pt: "Plataforma Orientada a Eventos",
      de: "Ereignisgesteuerte Plattform",
    },
    description: {
      en: "Distributed event processing platform using Apache Kafka for real-time data streaming and asynchronous communication between services.",
      pt: "Plataforma de processamento de eventos distribuidos usando Apache Kafka para streaming de dados em tempo real e comunicacao assincrona entre servicos.",
      de: "Verteilte Ereignisverarbeitungsplattform mit Apache Kafka fur Echtzeit-Datenstreaming und asynchrone Kommunikation zwischen Diensten.",
    },
    tech: ["Java", "Kafka", "PostgreSQL", "Kubernetes"],
    status: "active",
    link: "https://github.com/peixotomdb",
  },
  {
    id: "monitoring-dashboard",
    logoSlug: "SMD",
    coverImg: "/projects/monitoring-dashboard.jpg",
    title: {
      en: "System Monitoring Dashboard",
      pt: "Painel de Monitorizacao de Sistemas",
      de: "System-Uberwachungs-Dashboard",
    },
    description: {
      en: "Real-time monitoring and observability dashboard for distributed systems, featuring custom metrics, alerting, and trace visualization.",
      pt: "Painel de monitorizacao e observabilidade em tempo real para sistemas distribuidos, com metricas personalizadas, alertas e visualizacao de traces.",
      de: "Echtzeit-Uberwachungs- und Observability-Dashboard fur verteilte Systeme mit benutzerdefinierten Metriken, Benachrichtigungen und Trace-Visualisierung.",
    },
    tech: ["TypeScript", "Next.js", "Grafana", "Prometheus"],
    status: "active",
    link: "https://github.com/peixotomdb",
  },
  {
    id: "legacy-migration",
    logoSlug: "LM",
    coverImg: "/projects/legacy-migration.jpg",
    title: {
      en: "Legacy System Migration",
      pt: "Migracao de Sistema Legado",
      de: "Legacy-System-Migration",
    },
    description: {
      en: "Comprehensive migration strategy and tooling for transitioning monolithic applications to microservice-based architectures with zero downtime.",
      pt: "Estrategia e ferramentas abrangentes de migracao para transicao de aplicacoes monoliticas para arquiteturas baseadas em microsservicos com zero downtime.",
      de: "Umfassende Migrationsstrategie und Tools fur den Ubergang von monolithischen Anwendungen zu Microservice-basierten Architekturen ohne Ausfallzeit.",
    },
    tech: ["Java", "Spring Boot", "AWS", "Terraform"],
    status: "archived",
    link: "https://github.com/peixotomdb",
  },
  */
]
